import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, KeyRound, CheckCircle2, AlertCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";
    const [state, setState] = useState({ status: "verifying", email: "" });
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) { setState({ status: "invalid" }); return; }
        api.post("/auth/password-reset/verify", { token })
            .then(r => setState({ status: "valid", email: r.data.email }))
            .catch(() => setState({ status: "invalid" }));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (pw.length < 8) { setErr("La contraseña debe tener al menos 8 caracteres."); return; }
        if (pw !== pw2) { setErr("Las contraseñas no coinciden."); return; }
        setBusy(true);
        try {
            await api.post("/auth/password-reset/confirm", { token, new_password: pw });
            setDone(true);
            setTimeout(() => navigate("/login", { replace: true }), 2000);
        } catch (e2) {
            setErr(formatApiError(e2) || "Error al cambiar la contraseña");
        } finally { setBusy(false); }
    };

    if (state.status === "verifying") {
        return (
            <div className="min-h-screen bg-slate-950 text-slate-50 flex items-center justify-center">
                <Loader2 className="animate-spin text-slate-500"/>
            </div>
        );
    }

    if (state.status === "invalid") {
        return (
            <div className="min-h-screen bg-slate-950 text-slate-50 flex items-center justify-center p-6" data-testid="reset-page-invalid">
                <div className="w-full max-w-md rounded-2xl border border-red-500/40 bg-red-500/10 p-6 space-y-3 text-center">
                    <AlertCircle size={32} className="text-red-300 mx-auto"/>
                    <h1 className="font-display text-lg font-bold">Enlace inválido</h1>
                    <p className="text-sm text-red-200">El enlace es inválido o ha expirado. Solicita uno nuevo.</p>
                    <Link to="/forgot-password" data-testid="reset-request-new-link" className="inline-block text-sm text-brand-orange hover:underline">
                        Solicitar nuevo enlace
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950 text-slate-50 flex items-center justify-center p-6" data-testid="reset-password-page">
            <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900/80 p-6 space-y-4">
                <div className="flex items-center gap-2">
                    <KeyRound size={20} className="text-brand-orange"/>
                    <h1 className="font-display text-xl font-bold">Nueva contraseña</h1>
                </div>
                <p className="text-xs text-slate-400">Cuenta: <span className="font-mono">{state.email}</span></p>
                {done ? (
                    <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/40 p-4 text-sm text-emerald-100" data-testid="reset-success">
                        <CheckCircle2 size={20} className="inline mr-2"/> Contraseña actualizada. Redirigiendo…
                    </div>
                ) : (
                    <form onSubmit={submit} noValidate className="space-y-3">
                        <div>
                            <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">Nueva contraseña</label>
                            <input type="password" value={pw} onChange={e => setPw(e.target.value)}
                                data-testid="reset-new-password" required minLength={8}
                                className="w-full h-11 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 px-3"/>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">Confirmar contraseña</label>
                            <input type="password" value={pw2} onChange={e => setPw2(e.target.value)}
                                data-testid="reset-confirm-password" required
                                className="w-full h-11 rounded-lg bg-slate-950 border border-slate-700 text-slate-100 px-3"/>
                        </div>
                        {err && <div className="rounded-lg bg-red-500/10 border border-red-500/40 p-3 text-xs text-red-200" data-testid="reset-error">{err}</div>}
                        <button type="submit" disabled={busy} data-testid="reset-submit"
                            className="w-full h-11 rounded-lg bg-brand-orange text-slate-950 font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
                            {busy && <Loader2 className="animate-spin" size={14}/>}
                            Cambiar contraseña
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
